import type { BinarySyncLegajo, MarcaSync } from './services/sync-binary.service';

/**
 * Traduce una corrida de la sincronización con Binary (VEN-540) a frases que
 * se entiendan sin conocer el sistema: una oración de resumen arriba y, por
 * cada persona, qué le pasó, con valores legibles ("Cónyuge", no "5").
 *
 * Es puro a propósito: la ficha solo lo dibuja, y así se puede testear cada
 * frase sin montar la pantalla.
 */

export interface PersonaResumen {
  /** Clave estable para la lista (asociadoId, o afiId para un alta simulada). */
  clave: string;
  nombre: string;
  lineas: string[];
}

export interface ResumenSync {
  /** Oración de arriba. */
  frase: string;
  /** Personas con algo para contar, en el orden del legajo. */
  personas: PersonaResumen[];
  /** Nombres de las personas del legajo a las que no les cambió nada. */
  sinCambios: string[];
  /** Cuántas personas quedaron (o quedarían) vinculadas con su ficha en Binary. */
  vinculadas: number;
}

export interface PersonaDelLegajo {
  id: string;
  nombre: string;
}

const CAMPO: Record<string, string> = {
  apellSolt: 'Apellido',
  nombres: 'Nombres',
  fechaNacimiento: 'Fecha de nacimiento',
  sexo: 'Sexo',
  estadoCivil: 'Estado civil',
  tipoDoc: 'Tipo de documento',
  documento: 'Número de documento',
  cuil: 'CUIL',
  email: 'Email',
  areaCelular: 'Código de área del celular',
  numeroCelular: 'Número de celular',
  parentesco: 'Parentesco',
};

const VALOR: Record<string, Record<string, string>> = {
  parentesco: { '0': 'Titular', '5': 'Cónyuge', '7': 'Hijo/a', '13': 'Adherente' },
  sexo: { M: 'Masculino', F: 'Femenino' },
  estadoCivil: { S: 'Soltero/a', C: 'Casado/a', P: 'Separado/a o divorciado/a' },
  tipoDoc: { DNI: 'DNI', PA: 'Pasaporte' },
};

const campoLegible = (campo: string): string => CAMPO[campo] ?? campo;

/** El valor como lo lee una persona: códigos traducidos, el resto tal cual. */
export function valorLegible(campo: string, valor: string | null | undefined): string {
  const v = String(valor ?? '').trim();
  return VALOR[campo]?.[v.toUpperCase()] ?? VALOR[campo]?.[v] ?? v;
}

/** "1 dato" / "4 datos". */
function cuenta(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** Un valor para la línea del cambio: el nombre del código, o "(vacío)" si no había. */
function valorDeLinea(campo: string, valor: string | null | undefined): string {
  return String(valor ?? '').trim() ? valorLegible(campo, valor) : '(vacío)';
}

/**
 * "Parentesco: Cónyuge → Hijo/a". Sin punto final: los valores son emails y
 * números, y un punto pegado se lee como parte del dato. Si se aplicó o fue
 * simulación lo dice la frase de arriba, así que la línea es igual en los dos
 * casos (antes decía "era X, ahora es Y" / "es X, pasaría a ser Y" y con 25
 * campos costaba leerla).
 */
function lineaCambio(campo: string, antes: string, despues: string): string {
  return `${campoLegible(campo)}: ${valorDeLinea(campo, antes)} → ${valorDeLinea(campo, despues)}`;
}

/** Qué le pasó a la persona, para las marcas que no son la vinculación. */
function lineaMarca(m: MarcaSync, simulado: boolean): string {
  const donde = m.detalle ? ` ${m.detalle}` : ' otro legajo';
  const frases: Record<string, [string, string]> = {
    baja_binary: [
      'Figura dada de baja en Binary: se la marcó como dada de baja en el portal.',
      'Figura dada de baja en Binary: se la marcaría como dada de baja en el portal.',
    ],
    reactivado_binary: [
      'Figura activa en Binary: se le quitó la marca de baja.',
      'Figura activa en Binary: se le quitaría la marca de baja.',
    ],
    no_en_binary: [
      'No aparece en Binary: se la marcó como «No está en Binary».',
      'No aparece en Binary: se la marcaría como «No está en Binary».',
    ],
    vuelve_a_binary: [
      'Volvió a aparecer en Binary: se le quitó la marca «No está en Binary».',
      'Volvió a aparecer en Binary: se le quitaría la marca «No está en Binary».',
    ],
    movido_a_otro_legajo: [
      `En Binary pertenece a otro grupo familiar: se la pasó al${donde}.`,
      `En Binary pertenece a otro grupo familiar: se la pasaría al${donde}.`,
    ],
    traido_de_otro_legajo: [
      `En Binary pertenece a este grupo familiar: se la trajo desde el${donde}.`,
      `En Binary pertenece a este grupo familiar: se la traería desde el${donde}.`,
    ],
    retitularizado: ['Pasó a ser el titular del legajo.', 'Pasaría a ser el titular del legajo.'],
  };
  const par = frases[m.tipo];
  return par ? par[simulado ? 1 : 0] : m.tipo;
}

function lineaAlta(parentesco: string | null, simulado: boolean): string {
  const como = parentesco ? ` como ${valorLegible('parentesco', parentesco)}` : '';
  return simulado
    ? `No estaba en el portal y figura en Binary: se agregaría al legajo${como}.`
    : `No estaba en el portal y figura en Binary: se agregó al legajo${como}.`;
}

/** Conjuga según simulación y número: [real 1, real N, simulado 1, simulado N]. */
function conjugar(sim: boolean, n: number, formas: [string, string, string, string]): string {
  return formas[(sim ? 2 : 0) + (n === 1 ? 0 : 1)];
}

function partesDeFrase(
  sync: BinarySyncLegajo,
  personasConCambios: number,
  personasConEstado: number,
): string[] {
  const sim = sync.simulado;
  const partes: string[] = [];
  const nc = sync.cambios.length;
  if (nc > 0) {
    const verbo = conjugar(sim, nc, [
      'se actualizó',
      'se actualizaron',
      'se actualizaría',
      'se actualizarían',
    ]);
    partes.push(
      `${verbo} ${cuenta(nc, 'dato', 'datos')} de ${cuenta(personasConCambios, 'persona', 'personas')}`,
    );
  }
  const na = sync.altas.length;
  if (na > 0) {
    const verbo = conjugar(sim, na, ['se agregó', 'se agregaron', 'se agregaría', 'se agregarían']);
    partes.push(`${verbo} ${cuenta(na, 'persona', 'personas')} al legajo`);
  }
  if (personasConEstado > 0) {
    const verbo = sim ? 'cambiaría' : 'cambió';
    partes.push(`${verbo} la situación de ${cuenta(personasConEstado, 'persona', 'personas')}`);
  }
  return partes;
}

/** "a", "a y b", "a, b y c". */
function unirConY(partes: string[]): string {
  if (partes.length <= 1) return partes[0] ?? '';
  return `${partes.slice(0, -1).join(', ')} y ${partes.at(-1)}`;
}

function fraseResumen(
  sync: BinarySyncLegajo,
  personasConCambios: number,
  personasConEstado: number,
): string {
  const sim = sync.simulado;
  const prefijo = sim ? 'Simulación, no se aplicó nada: ' : '';
  const partes = partesDeFrase(sync, personasConCambios, personasConEstado);
  if (partes.length === 0) {
    return `${prefijo}Los datos del portal coinciden con Binary. No ${sim ? 'habría' : 'hubo'} nada para actualizar.`;
  }
  const oracion = `${unirConY(partes)}.`;
  return sim ? `${prefijo}${oracion}` : oracion.charAt(0).toUpperCase() + oracion.slice(1);
}

/**
 * Arma el resumen legible de una corrida. `personas` son las del legajo
 * (titular primero), para respetar su orden y listar a las que no cambiaron.
 */
export function resumirSync(sync: BinarySyncLegajo, personas: PersonaDelLegajo[]): ResumenSync {
  const sim = sync.simulado;
  const porPersona = new Map<string, PersonaResumen>();
  const de = (clave: string, nombre: string): PersonaResumen => {
    const p = porPersona.get(clave) ?? { clave, nombre, lineas: [] };
    porPersona.set(clave, p);
    return p;
  };

  for (const c of sync.cambios) de(c.asociadoId, c.nombre).lineas.push(lineaCambio(c.campo, c.antes, c.despues));
  const conEstado = new Set<string>();
  for (const m of sync.marcas) {
    if (m.tipo === 'vinculado') continue;
    de(m.asociadoId, m.nombre).lineas.push(lineaMarca(m, sim));
    conEstado.add(m.asociadoId);
  }
  for (const o of sync.omitidos) {
    de(o.asociadoId, o.nombre).lineas.push(
      `${campoLegible(o.campo)}: Binary tiene otro dato, pero no se cambió. ${o.motivo}`,
    );
  }
  for (const a of sync.altas) de(a.asociadoId || `afi:${a.afiId}`, a.nombre).lineas.push(lineaAlta(a.parentesco, sim));

  // Orden del legajo primero; lo que no esté en el legajo (altas nuevas) al final.
  const orden = new Map(personas.map((p, i) => [p.id, i]));
  const conAlgo = [...porPersona.values()].sort(
    (x, y) => (orden.get(x.clave) ?? 999) - (orden.get(y.clave) ?? 999),
  );

  return {
    frase: fraseResumen(sync, new Set(sync.cambios.map((c) => c.asociadoId)).size, conEstado.size),
    personas: conAlgo,
    sinCambios: personas.filter((p) => !porPersona.has(p.id)).map((p) => p.nombre),
    vinculadas: sync.marcas.filter((m) => m.tipo === 'vinculado').length,
  };
}

/** La nota al pie sobre la vinculación, que no cambia ningún dato visible. */
export function textoVinculadas(n: number, simulado: boolean): string {
  if (n <= 0) return '';
  const sujeto =
    n === 1
      ? `1 persona ${simulado ? 'quedaría vinculada' : 'quedó vinculada'}`
      : `${n} personas ${simulado ? 'quedarían vinculadas' : 'quedaron vinculadas'}`;
  return `${sujeto} con su ficha en Binary: se guardó su número de afiliado de Binary. No cambia ningún dato visible; sirve para encontrarla${n === 1 ? '' : 's'} directo la próxima vez.`;
}
